import * as rxjs from 'rxjs';

import { Dependencies, Keys, Mutations, Stores } from '@pitaman71/omniglot-live-data'; 
import * as Elevated from '@pitaman71/omniglot-introspect';
import * as CodeInstruments from '@pitaman71/omniglot-code-instruments';

export var logEnable = () => false;
let debugMode = false;
const __moduleName__ = "omniglot-live-react.WebStorage";

export function toPath(path: (string|number)[]): string {
    if(path.length === 0) return '';
    const last = path[path.length - 1];
    if(typeof(last) === 'string') {
        if(path.length > 1) {
            return toPath(path.slice(0,path.length - 1)) + '.' + last;
        } else {
            return last;
        }
    } else {
        return toPath(path.slice(0,path.length - 1)) + `[${last}]`;
    }
}

export class Reader<ExpectedType extends Elevated.Serializable> extends Elevated.Visitor<ExpectedType> {
    factory: Elevated.Factory<ExpectedType>;
    prefix: (string|number)[];
    storage: Storage;
    obj: ExpectedType|undefined;
    refs: Map<string, Map<string, Elevated.Serializable>>;
    is_ref: boolean;

    // Reads in-memory representation from semi-self-describing JSON by introspecting objects using their marshal method
    constructor(
        factory: Elevated.Factory<ExpectedType>, 
        prefix: (string|number)[],
        storage: Storage, 
        refs?: Map<string, Map<string, Elevated.Serializable>>
    ) {
        super();
        this.factory = factory;
        this.prefix = prefix;
        this.storage = storage;
        this.refs = refs ? refs : new Map();
        this.is_ref = false;
    }

    begin(obj: ExpectedType) {
        // Must be called at the start of any marshal method. Tells this object that we are visiting the body of that object next
        if(!this.obj) {
            this.obj = obj;
        }
        const className = this.factory.getClassSpec();
        if(!this.refs.has(className)) {
            this.refs.set(className, new Map());
        }
        const by_id = this.refs.get(className);
        const key = this.storage.getItem(toPath([ ...this.prefix, '__id__' ]));
        if(key) {
            if(by_id?.has(key)) {
                this.obj = by_id.get(key) as ExpectedType;
                this.is_ref = true;
            } else {
                by_id?.set(key, this.obj);
            }
        }
    }

    end(obj:ExpectedType) {
        //Must be called at the end of any marshal method. Tells this object that we are done visiting the body of that object
    }

    owner(target: ExpectedType, ownerPropName: string): void {}

    verbatim<DataType>(
        target: Elevated.Serializable,
        getValue: (target: Elevated.Serializable) => any,
        setValue: (target: Elevated.Serializable, value: any) => void
    ): void {
        // For the in-memory object currently being read from JSON, read the value of attribute :attr_name from JSON propery attr_name.
        // Expect that the attribute value is probably not a reference to a shared object (though it may be)

        new CodeInstruments.Task.Task(`${__moduleName__}.Reader.verbatim`).logs(console.log, logEnable).returns({}, () => {
            const asJSONString = this.storage.getItem(toPath([ ...this.prefix, '__verbatim__' ]));
            if(!asJSONString) {
                setValue(target, undefined);
            } else {
                setValue(target, JSON.parse(asJSONString));
            }
        });
    }    

    primitive<PropType>(target: any, propName: string, fromString?: (initializer:string) => PropType): void {
        // For the in-memory object currently being read from JSON, read the value of attribute :attr_name from JSON propery attr_name.
        // Expect that the attribute value is probably not a reference to a shared object (though it may be)

        new CodeInstruments.Task.Task(`${__moduleName__}.Reader.primitive(${propName})`).logs(console.log, logEnable).returns({}, () => {
            target[propName] = read([ ...this.prefix, propName ], this.storage);
        });
    }    

    reference<ElementType extends Elevated.Serializable>(
        elementFactory: Elevated.Factory<ElementType>,
        propName: string
    ): void {
        const target:any = this.obj;
        const sub = new Reader<Elevated.Reference<ElementType>>(Elevated.Reference.Factory, [ ...this.prefix, propName ], this.storage, this.refs )
        target[propName] = sub.read();
    }

    scalar<ElementType extends Elevated.Serializable>(
        elementFactory: Elevated.Factory<ElementType>,
        propName: string        
    ): void {
        // For the in-memory object currently being read from JSON, read the value of attribute :attr_name from JSON propery attr_name.
        // Expect that the attribute value is probably not a reference to a shared object (though it may be)
        new CodeInstruments.Task.Task(`${__moduleName__}.Reader.scalar(${propName})`).logs(console.log, logEnable).returns({}, () => {
            const reader = new Reader<ElementType>(elementFactory, [ ...this.prefix, propName ], this.storage, this.refs);
            const target: any = this.obj;
            target[propName] = reader.read();
        });
    }

    array<ElementType extends Elevated.Serializable>(
        elementFactory: Elevated.Factory<ElementType>,
        propName: string        
    ): void {
        // For the in-memory object currently being read from JSON, read the value of attribute :attr_name from JSON propery attr_name
        // Expect that the attribute value is probably a reference to a shared object (though it may not be)

        new CodeInstruments.Task.Task(`${__moduleName__}.Reader.array(${propName})`).logs(console.log, logEnable).returns({}, () => {
            const target:any = this.obj;
            const lengthString = this.storage.getItem(toPath([ ...this.prefix, '__length__' ]));
            if(lengthString === null) {
                throw new Error('No JSON here');
            } else {
                const length = parseInt(lengthString);
                target[propName] = [];
                for(let index = 0; index < length; index++) {
                    const reader = new Reader<ElementType>(elementFactory, [ ...this.prefix, propName, index ], this.storage, this.refs);
                    target[propName].push(reader.read());
                }
            }
        });
    }

    map<ElementType extends Elevated.Serializable>(
        elementFactory: Elevated.Factory<ElementType>,
        propName: string        
    ): void {
        // For the in-memory object currently being read from JSON, read the value of attribute :attr_name from JSON propery attr_name
        // Expect that the attribute value is probably a reference to a shared object (though it may not be)

        new CodeInstruments.Task.Task(`${__moduleName__}.Reader.map(${propName})`).logs(console.log, logEnable).returns({}, () => {
            const target:any = this.obj;
            const keysString = this.storage.getItem(toPath([ ...this.prefix, '__keys__' ]));
            if(keysString === null) {
                throw new Error('No JSON here');
            } else {
                const keys: string[] = JSON.parse(keysString);
                target[propName] = {};
                const newValue = keys.forEach((key: string) => {
                    const reader = new Reader<ElementType>(elementFactory, [ ...this.prefix, key ], this.storage, this.refs);
                    target[key] = reader.read();
                });
            }
        });
    }

    read(): undefined|null|ExpectedType {
        return new CodeInstruments.Task.Task(`${__moduleName__}.Reader.read`).logs(console.log, logEnable).returns({}, () => {
            const format = this.storage.getItem(toPath(this.prefix));
            if(format === null) {
                return undefined;
            } else if(format === 'null') {
                return null;
            } else if(format === 'serializable') {
                this.obj = this.factory.make();
                this.obj.marshal(this);
                return this.obj;
            } else {
                this.obj = this.factory.make();
                this.obj.marshal(this);
                return this.obj;
            }
        });
    }
}

export class Writer<ExpectedType extends Elevated.Serializable> extends Elevated.Visitor<ExpectedType> {
    factory: Elevated.Factory<ExpectedType>;
    prefix: (string|number)[];
    storage: Storage;
    obj:ExpectedType;
    refs: Map<string, Map<string, Elevated.Serializable>>;
    is_ref?: boolean;

    // Reads in-memory representation from semi-self-describing JSON by introspecting objects using their marshal method
    constructor(
        factory: Elevated.Factory<ExpectedType>, 
        prefix: (string|number)[],
        storage: Storage,
        obj: ExpectedType, 
        refs?: Map<string, Map<string, Elevated.Serializable>>
    ) {
        super();
        this.factory = factory;
        this.prefix = prefix;
        this.storage = storage;
        this.obj = obj;
        this.refs = refs ? refs : new Map();
    }

    _property(suffix: string): string {
        return this.prefix + suffix;
    }

    begin(obj: ExpectedType) {
        // Must be called at the start of any marshal method. Tells this object that we are visiting the body of that object next"""
        
        const classSpec = this.factory.getClassSpec();
        if(!this.refs.has(classSpec)) {
            this.refs.set(classSpec, new Map());
        }
        if(!classSpec) {
            throw new Error(`Cannot find class name for ${typeof obj}`);
        }
        this.storage.setItem(toPath([ ...this.prefix, '__class__' ]), classSpec);
        let key = obj.getGlobalId();
        if(key !== null) {
            this.is_ref = this.refs.get(classSpec)?.has(key.toString()) || false;
            this.storage.setItem(toPath([ ...this.prefix, '__id__' ]), key.toString())
        } else {
            this.is_ref = false;
        }
        this.storage.setItem(toPath([ ...this.prefix, '__is_ref__' ]), this.is_ref.toString());
    }

    end(obj: ExpectedType) {
    }

    owner(target: ExpectedType, ownerPropName: string): void {}

    verbatim<DataType>(
        target: Elevated.Serializable,
        getValue: (target: Elevated.Serializable) => any,
        setValue: (target: Elevated.Serializable, value: any) => void
    ): void {
        new CodeInstruments.Task.Task(`${__moduleName__}.Writer.verbatim`).logs(console.log, logEnable).returns({}, () => {
            this.storage.setItem(toPath([ ...this.prefix, '__verbatim__' ]), JSON.stringify(getValue(target)))
        });
    }

    primitive<PropType>(target: any, propName: string, fromString?: (initializer:string) => PropType): void {
        new CodeInstruments.Task.Task(`${__moduleName__}.Writer.primitive(${propName})`).logs(console.log, logEnable).returns({}, () => {
            if(!this.is_ref) {
                write([ ...this.prefix, propName ], this.storage, target[propName]);
            }
        });
    }

    reference<ElementType extends Elevated.Serializable>(
        elementFactory: Elevated.Factory<ElementType>,
        propName: string
    ): void {
        const target:any = this.obj;
        const sub = new Writer<Elevated.Reference<ElementType>>(Elevated.Reference.Factory, [ ...this.prefix, propName ], this.storage, target[propName], this.refs )
        sub.write();
    }

    scalar<ElementType extends Elevated.Serializable>(
        elementFactory: Elevated.Factory<ElementType>,
        propName: string        
    ): void {
        // For the in-memory object currently being written to JSON, write the value of attribute :attr_name to JSON propery attr_name.
        // Expect that the attribute value is probably not a reference to a shared object (though it may be)

        new CodeInstruments.Task.Task(`${__moduleName__}.Writer.scalar(${propName})`).logs(console.log, logEnable).returns({}, () => {
            if(this.is_ref) return;
            const target: any = this.obj;
            if(target.hasOwnProperty(propName)) {
                const writer = new Writer<ElementType>(elementFactory, [ ...this.prefix, propName ], this.storage, target[propName], this.refs);
                writer.write();
            }
        });
    }

    array<ElementType extends Elevated.Serializable>(
        elementFactory: Elevated.Factory<ElementType>,
        propName: string        
    ): void {
        // For the in-memory object currently being written to JSON, write the value of attribute :attr_name to JSON propery attr_name
        // Expect that the attribute value is probably a reference to a shared object (though it may not be)

        new CodeInstruments.Task.Task(`${__moduleName__}.Writer.array(${propName})`).logs(console.log, logEnable).returns({}, () => {
            if(this.is_ref) return;
            const target:any = this.obj;
            if(target.hasOwnProperty(propName)) {
                this.storage.setItem(toPath([ ...this.prefix, '__length__' ]), target[propName].length.toString());
                target[propName].forEach((item: ElementType, index: number) => {
                    const writer = new Writer<ElementType>(elementFactory, [ ...this.prefix, propName, index ], this.storage, target[propName][index], this.refs);
                    writer.write();
                });
            }
        });
    }

    map<ElementType extends Elevated.Serializable>(
        elementFactory: Elevated.Factory<ElementType>,
        propName: string        
    ): void {
        // For the in-memory object currently being written to JSON, write the value of attribute :attr_name to JSON propery attr_name
        // Expect that the attribute value is probably a reference to a shared object (though it may not be)

        new CodeInstruments.Task.Task(`${__moduleName__}.Writer.map(${propName})`).logs(console.log, logEnable).returns({}, () => {
            if(this.is_ref) return;
            const target:any = this.obj;
            if(target.hasOwnProperty(propName)) {
                const keys = Object.getOwnPropertyNames(target[propName]);
                this.storage.setItem(toPath([ ...this.prefix, '__keys__']), JSON.stringify(keys));
                keys.forEach((key: string) => {
                    const writer = new Writer<ElementType>(elementFactory, [ ...this.prefix, propName, key ], this.storage, target[propName][key], this.refs);
                    writer.write();
                });
            }
        });
    }

    write(): void {
        return new CodeInstruments.Task.Task(`${__moduleName__}.Writer.write`).logs(console.log, logEnable).returns({ obj: this.obj?.toString() }, () => {
            if(this.obj === undefined) {
                // skip, nothing to do
            } else if(this.obj === null) {
                this.storage.setItem(toPath(this.prefix), 'null');
            } else if(this.obj instanceof Elevated.Serializable) {
                this.storage.setItem(toPath(this.prefix), 'serializable');
                this.obj.marshal(this);
            } else {
                this.storage.setItem(toPath(this.prefix), Elevated.JSONMarshal.toJSON(this.obj));
            }
        });
    }
}

export function read(prefix: (string|number)[], storage: Storage): any {
    const jsonString = storage.getItem(toPath(prefix));
    if(jsonString === null) {
        return undefined;
    } else if(jsonString === 'null') {
        return null;
    } else if(jsonString === 'serializable') {
        const className = storage.getItem(toPath([...prefix, '__class__' ]));
        if(className) {
            const factory = Elevated.factories.getFactory(className);
            const reader = new Reader<Elevated.Serializable>(factory, prefix, storage);
            return reader.read();
        }
    } else if(jsonString === 'set') {
        const asString = storage.getItem(toPath([ ... prefix, 'values' ]));
        if(!asString) return undefined;
        return new Set(Elevated.JSONMarshal.fromString(asString));
    }
    return Elevated.JSONMarshal.fromString(jsonString);
}

export function write(prefix: (string|number)[], storage: Storage, obj: any): void {
    if(obj === undefined) {
        // skip it
    } else if(obj === null) {
        storage.setItem(toPath(prefix), 'null');
    } else {
        if(obj instanceof Elevated.Serializable) {
            storage.setItem(toPath(prefix), 'serializable');
            const factory = obj.getFactory();
            const writer = new Writer<Elevated.Serializable>(factory, prefix, storage, obj);
            writer.write();
        } else if(obj instanceof Set) {
            storage.setItem(toPath(prefix), 'set');
            storage.setItem(toPath([ ... prefix, 'values' ]), Elevated.JSONMarshal.toString(Array.from(obj)));
        } else {
            storage.setItem(toPath(prefix), Elevated.JSONMarshal.toString(obj));
        }
    }
}

export class Journal implements Stores.Persist {
    prefix: string;
    storage: Storage;
    onPullRequest: rxjs.Subject<any>;

    constructor(
        prefix: string,
        storage: Storage,
    ) {
        this.prefix = prefix;
        this.storage = storage;
        this.onPullRequest = Dependencies.makeSubject();
    }

    watchWindow(window: Window) {
        return window.addEventListener('storage', (event: any) => {
            this.onPullRequest.next({});
        })        
    }

    getHeadTableProps(key: Keys.BaseKey) {
        return {
            firstId: () => [ this.prefix, key.toString(), 'firstId' ],
            lastId: () => [ this.prefix, key.toString(), 'lastId' ]
        };
    }

    getChangesTableProps(key: Keys.BaseKey) {
        return {
            mutations: (mutationId: string) => [ this.prefix, key.toString(), 'mutations', mutationId ]
        };
    }

    bind<ValueType>() : Promise<void> {
        return Promise.resolve();
    }

    clear() : Promise<void> {
        const keys = Object.keys(this.storage);
        for(let key of keys) {
            if(key.startsWith(this.prefix+'.')) {
                this.storage.removeItem(key);
            }
        }
        return Promise.resolve();
    }

    _load<MutationType extends Mutations.BaseMutation>(
        key: Keys.BaseKey,
        ref: Elevated.Reference<MutationType>
    ): Promise<MutationType> {
        const globalId = ref.getGlobalId();
        if(globalId === null) return Promise.reject('Cannot load a null reference');
        const persistKey = this.getChangesTableProps(key).mutations(globalId.toString());
        const mutation = read(
            persistKey,
            this.storage
        );
        if(mutation === null || mutation === undefined) {
            throw new Error(`Unable to load Mutation with key ${toPath(persistKey)}`);
        }
        return Promise.resolve(mutation);
    }

    pull_<MutationType extends Mutations.BaseMutation>(
        key: Keys.BaseKey,
        until: Elevated.Reference<MutationType>,
        toPull: Elevated.Reference<MutationType>,
        receive: rxjs.Observer<MutationType>
    ): Promise<Elevated.Reference<MutationType>> {
        if(toPull.pointsTo(until) && until._def) {
            if(!toPull._def) {
                toPull._def = until._def;
            }
            return Promise.resolve(until);
        } else {
            return this._load(key, toPull).then((mutation) => {
                if(!mutation) {
                    return Promise.reject({ err: 'WebStorage response is missing required "Item" property'});
                }
                return !mutation.prev.isValid() 
                    ? Promise.resolve({ mutation, last: Elevated.Reference.from(mutation) })
                    : this.pull_(key, until, mutation.prev, receive).then((last) => ({ mutation, last: Elevated.Reference.from(mutation) }))
                ;
            }).then((args: { mutation: MutationType, last: Elevated.Reference<MutationType>}) => {
                if(debugMode) console.log(`React Storage REPLAY ${key} ${args.mutation}`)
                receive.next(args.mutation);
                return args.last;
            }).catch(err => {
                return Promise.reject(err);
            })
        }
    }

    replay<MutationType extends Mutations.BaseMutation>(
        key: Keys.BaseKey,
        receive: rxjs.Observer<MutationType>
    ): Promise<Elevated.Reference<MutationType>> {
        const props = this.getHeadTableProps(key);
        const reader = new Reader<Elevated.Reference<MutationType>>(
            Elevated.Reference.Factory,
            props.lastId(), 
            this.storage
        )
        const mostRecentMutation = reader.read();
        if(debugMode) console.log(`REPLAY ${key} LocalStorage[${props.lastId().join('.')}] = ${mostRecentMutation}`)
        if(mostRecentMutation === undefined || mostRecentMutation === null || !mostRecentMutation.isValid()) {
            return Promise.resolve(Elevated.Reference.fromNull<MutationType>());
        } else {
            return this.pull_(key, Elevated.Reference.fromNull(), mostRecentMutation, receive);
        }
    }

    pull<MutationType extends Mutations.BaseMutation> (
        key: Keys.BaseKey,
        since: Elevated.Reference<MutationType>,
        receive: rxjs.Observer<MutationType>,
    ): Promise<Elevated.Reference<MutationType>> {
        const props = this.getHeadTableProps(key);
        const reader = new Reader<Elevated.Reference<MutationType>>(
            Elevated.Reference.Factory,
            props.lastId(), 
            this.storage
        )
        const lastMutationRef_ = reader.read();
        if(debugMode) console.log(`PULLED ${key} LocalStorage[${props.lastId().join('.')}] = ${lastMutationRef_}`)
        if(lastMutationRef_ === undefined || lastMutationRef_ === null || !lastMutationRef_.isValid()) {
            return Promise.resolve(since);
        }
        return this.pull_(key, since, lastMutationRef_, receive);
    }

    storeMutation_<MutationType extends Mutations.BaseMutation>(
        key: Keys.BaseKey,
        mutation: MutationType
    ) {
        const globalId = mutation.getGlobalId();
        if(globalId === null) {
            return Promise.reject(`Mutation is not null but has no global id`);
        }
        const writer = new Writer(
            mutation.getFactory(),
            this.getChangesTableProps(key).mutations(globalId.toString()),
            this.storage,
            mutation
        );
        const asJSON = writer.write();
        //console.log(`WRITE LocalStorage[${this.getChangesTableProps().mutations(globalId.toString())}] = ${mutation}`)
        if(mutation.prev.isValid()) {
            const prevId = mutation.prev.getGlobalId()?.toString();
            if(!prevId) {
                return Promise.reject(`Mutation.prev is not null but has no global id`);
            }
            
            write(
                [ ...this.getChangesTableProps(key).mutations(prevId), 'next' ],
                this.storage,
                Elevated.Reference.from(mutation)
            );
            //console.log(`UPDATE LocalStorage[${this.getChangesTableProps().mutations(prevPath)}] = ${JSON.stringify(asJSON)}`)
            return Promise.resolve(mutation);
        }
        return Promise.resolve(mutation);
    }

    push<MutationType extends Mutations.BaseMutation> (
        key: Keys.BaseKey,
        ...suffix: MutationType[]
    ): Promise<MutationType> {
        if(suffix.length === 0) {
            return Promise.reject({ err: `WebStorage.Store.push called with empty suffix` });
        } else {
            const dbOutcomes = Promise.all(suffix.map((mutation: MutationType) => {
                return this.storeMutation_(key, mutation).then(() => {
                    return suffix[suffix.length - 1];
                }).catch((err) => {
                    return Promise.reject({ err });
                });
            }));
            return dbOutcomes.then((outcomes: MutationType[]) => {
                const props = this.getHeadTableProps(key);
                const mutation = outcomes[outcomes.length - 1];
                const ref = Elevated.Reference.from(mutation);
                const firstId = new Reader(Elevated.Reference.Factory, props.firstId(), this.storage).read();
                const lastId = new Reader(Elevated.Reference.Factory, props.lastId(), this.storage);
                if(!firstId || !firstId.isValid())
                    write(props.firstId(), this.storage, ref);
                write(props.lastId(), this.storage, ref);
                if(debugMode) console.log(`WRITE ${key} LocalStorage[${props.lastId()}] = ${mutation}`)
                if(outcomes.length > 0)
                    this.onPullRequest.next(outcomes);
                return mutation;
            });
        }
    }
 
    _normalize<MutationType extends Mutations.BaseMutation>(key: Keys.BaseKey, mutation: MutationType) : Promise<any> {
        return Promise.resolve();
    }

    normalize(key: Keys.BaseKey) : Promise<any> {
        const props = this.getHeadTableProps(key);
        const firstId = this.storage.get(props.firstId());
        const lastId = this.storage.get(props.lastId());
        if(firstId !== lastId) 
            return this._load(key, firstId).then(firstMutation => this._normalize(key, firstMutation));
        return Promise.resolve();
    }
}
